import React, { useState, Suspense } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { TechScoreBadge } from './components/Badges';
import { AnalysisRow } from './useGlobalAnalysis';
import type { FearGreedData } from './useFearGreed';
import './MobileAnalysis.css';

interface MobileAnalysisProps {
    analysis: {
        meanYtd: number;
        pctUp: number;
        avgVol30: number;
        marketMood: number | null;
        gems: { yieldKings: AnalysisRow[]; momentumStars: AnalysisRow[]; safeHavens: AnalysisRow[] };
        seasonality: { month: number; avgChange: number; winRate: number }[];
        sectorData: { sector: string; avgYtd: number; avgYield: number; avgVol: number; count: number }[];
        rows: AnalysisRow[];
    };
    rows: AnalysisRow[];
    theme: Record<string, string>;
    /** Rodage admin : si défini, l'indice Peur & Avidité remplace la jauge « Sentiment de Marché ». */
    fearGreed?: { data: FearGreedData | null } | null;
    searchTerm: string;
    setSearchTerm: (v: string) => void;
    sortColumn: string;
    setSortColumn: (v: string) => void;
    sortDirection: 'asc' | 'desc';
    setSortDirection: (v: 'asc' | 'desc') => void;
    exportCSVRows: () => void;
    // Filters
    showFilters: boolean;
    setShowFilters: (v: boolean) => void;
    filterSector: string;
    setFilterSector: (v: string) => void;
    filterRSI: string;
    setFilterRSI: (v: string) => void;
    filterYTD: string;
    setFilterYTD: (v: string) => void;
    filterDivYield: string;
    setFilterDivYield: (v: string) => void;
    trackAction: (action: string) => void;
}

// Lazy load heavy components
const LazyMarketMoodGauge = React.lazy(() =>
    import('./components/MarketMoodGauge').then(m => ({ default: m.MarketMoodGauge }))
);
const LazyFearGreedGauge = React.lazy(() =>
    import('./components/FearGreedGauge').then(m => ({ default: m.FearGreedGauge }))
);

const MobileAnalysis: React.FC<MobileAnalysisProps> = ({
    analysis,
    rows,
    theme,
    fearGreed,
    searchTerm,
    setSearchTerm,
    sortColumn,
    setSortColumn,
    sortDirection,
    setSortDirection,
    exportCSVRows,
    showFilters,
    setShowFilters,
    filterSector,
    setFilterSector,
    filterRSI,
    setFilterRSI,
    filterYTD,
    setFilterYTD,
    filterDivYield,
    setFilterDivYield,
    trackAction,
}) => {
    const { meanYtd, pctUp, avgVol30, marketMood, gems, seasonality, sectorData } = analysis;
    const [seasonOpen, setSeasonOpen] = useState(false);
    const [heatmapOpen, setHeatmapOpen] = useState(false);

    // Filtered rows (simplified filter set for mobile)
    const filteredRows = rows.filter((r: AnalysisRow) => {
        const matchSearch = r.symbole.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.nom || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchSector = filterSector === 'all' || r.secteur === filterSector;
        const matchRSI = filterRSI === 'all' ||
            (filterRSI === 'oversold' && r.rsi_last && r.rsi_last < 30) ||
            (filterRSI === 'neutral' && r.rsi_last && r.rsi_last >= 30 && r.rsi_last <= 70) ||
            (filterRSI === 'overbought' && r.rsi_last && r.rsi_last > 70);
        const matchYTD = filterYTD === 'all' ||
            (filterYTD === 'positive' && r.ytd && r.ytd > 0) ||
            (filterYTD === 'negative' && r.ytd && r.ytd < 0);
        const matchDiv = filterDivYield === 'all' ||
            (filterDivYield === 'high' && r.div_yield && r.div_yield >= 5) ||
            (filterDivYield === 'medium' && r.div_yield && r.div_yield >= 2 && r.div_yield < 5) ||
            (filterDivYield === 'low' && r.div_yield && r.div_yield > 0 && r.div_yield < 2);
        return matchSearch && matchSector && matchRSI && matchYTD && matchDiv;
    }).sort((a: AnalysisRow, b: AnalysisRow) => {
        const aVal = (a as Record<string, unknown>)[sortColumn] ?? 0;
        const bVal = (b as Record<string, unknown>)[sortColumn] ?? 0;
        if (sortColumn === 'symbole') {
            return sortDirection === 'asc'
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        }
        return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    return (
        <div className="ma-container">

            {/* 1. MARKET MOOD — Key signal */}
            <div className="ma-mood-card">
                <Suspense fallback={<div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>Chargement...</div>}>
                    {fearGreed
                        ? <LazyFearGreedGauge data={fearGreed.data} theme={theme} />
                        : <LazyMarketMoodGauge score={marketMood} theme={theme} />}
                </Suspense>
            </div>

            {/* 2. KPI STRIP — 3 essential metrics */}
            <div className="ma-kpi-row">
                <div className="ma-kpi-card">
                    <span className="ma-kpi-label">Perf. Moy.</span>
                    <span className={`ma-kpi-value ${meanYtd >= 0 ? 'positive' : 'negative'}`}>{meanYtd.toFixed(2)}%</span>
                </div>
                <div className="ma-kpi-card">
                    <span className="ma-kpi-label">% Hausse</span>
                    <span className="ma-kpi-value positive">{pctUp.toFixed(0)}%</span>
                </div>
                <div className="ma-kpi-card">
                    <span className="ma-kpi-label">Volatilité</span>
                    <span className="ma-kpi-value neutral">{(avgVol30 * 100).toFixed(1)}%</span>
                </div>
            </div>

            {/* 3. GEMS — Top picks */}
            {gems && (gems.yieldKings?.length > 0 || gems.momentumStars?.length > 0) && (
                <div className="ma-gems">
                    <h3 className="ma-section-title">💎 Pépites identifiées</h3>
                    <div className="ma-gems-list">
                        {[...gems.yieldKings, ...gems.momentumStars, ...gems.safeHavens].slice(0, 5).map((g: AnalysisRow, i: number) => (
                            <div key={i} className="ma-gem-card">
                                <div className="ma-gem-header">
                                    <span className="ma-gem-symbol">{g.symbole}</span>
                                    <span className={`ma-gem-ytd ${(g.ytd ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                                        {(g.ytd ?? 0) > 0 ? '+' : ''}{(g.ytd ?? 0).toFixed(2)}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 4. SECTOR HEATMAP — Collapsible */}
            <div className="ma-collapsible">
                <button className="ma-collapse-toggle" onClick={() => setHeatmapOpen(!heatmapOpen)}>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        🗺️ Carte Thermique Sectorielle
                        <span 
                            title="Méthodologie Kanea : Performance moyenne équipondérée (Equal-Weight). Reflète la largeur du marché (Market Breadth) et évite le biais de concentration des méga-caps. Sources: 'The Cross-Section of Expected Stock Returns' (Fama & French, 1992) & 'Fundamental Indexation' (Arnott et al., 2005)."
                            style={{ fontSize: 16, color: '#3b82f6', marginLeft: 8, padding: '0 4px' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                alert("Méthodologie Kanea : Performance moyenne équipondérée (Equal-Weight).\nContrairement au BOC (pondéré par capitalisation), cette approche reflète la vraie tendance du marché et évite l'impact démesuré des méga-capitalisations.\n\nSources : 'The Cross-Section of Expected Stock Returns' (Fama & French, 1992) & 'Fundamental Indexation' (Arnott et al., 2005).");
                            }}
                        >
                            ⓘ
                        </span>
                    </span>
                    {heatmapOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {heatmapOpen && (
                    <div className="ma-collapse-content">
                        {sectorData && sectorData.map((s, i: number) => (
                            <div key={i} className="ma-sector-row">
                                <span className="ma-sector-name">{s.sector}</span>
                                <div className="ma-sector-bar-wrap">
                                    <div
                                        className={`ma-sector-bar ${s.avgYtd >= 0 ? 'positive' : 'negative'}`}
                                        style={{ width: `${Math.min(Math.abs(s.avgYtd) * 3, 100)}%` }}
                                    />
                                </div>
                                <span className={`ma-sector-value ${s.avgYtd >= 0 ? 'positive' : 'negative'}`}>
                                    {s.avgYtd >= 0 ? '+' : ''}{s.avgYtd.toFixed(1)}%
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 5. SEASONALITY — Collapsible */}
            <div className="ma-collapsible">
                <button className="ma-collapse-toggle" onClick={() => setSeasonOpen(!seasonOpen)}>
                    <span>📅 Cycle Saisonnier</span>
                    {seasonOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {seasonOpen && (
                    <div className="ma-collapse-content">
                        {seasonality.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 15, fontSize: 12 }}>
                                Aucune donnée saisonnière disponible.
                            </div>
                        ) : (
                            seasonality.map((m) => (
                                <div key={m.month} className="ma-season-row">
                                    <span className="ma-season-month">
                                        {new Date(0, m.month).toLocaleString('fr-FR', { month: 'short' })}
                                    </span>
                                    <div className="ma-season-bar-wrap">
                                        <div
                                            className={`ma-season-bar ${m.avgChange >= 0 ? 'positive' : 'negative'}`}
                                            style={{ width: `${Math.min(Math.abs(m.avgChange) * 10, 100)}%` }}
                                        />
                                    </div>
                                    <span className={`ma-season-value ${m.avgChange >= 0 ? 'positive' : 'negative'}`}>
                                        {m.avgChange > 0 ? '+' : ''}{m.avgChange.toFixed(2)}%
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* 6. SCANNER PRO — Cards instead of table */}
            <div className="ma-scanner">
                <h3 className="ma-section-title">🔍 Scanner Pro</h3>

                {/* Search */}
                <input
                    type="text"
                    placeholder="Rechercher par symbole..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ma-scanner-search"
                />

                {/* Quick Filters */}
                <div className="ma-scanner-filters">
                    <button
                        className={`ma-filter-btn ${showFilters ? 'active' : ''}`}
                        onClick={() => {
                            setShowFilters(!showFilters);
                            if (!showFilters) trackAction('USE_SCREENER_PRO');
                        }}
                    >
                        ⚙️ Filtres {showFilters ? '▲' : '▼'}
                    </button>
                    <button className="ma-filter-btn" onClick={exportCSVRows}>📥 CSV</button>
                    <span className="ma-filter-count">{filteredRows.length} résultats</span>
                </div>

                {showFilters && (
                    <div className="ma-filters-panel">
                        <div className="ma-filter-group">
                            <label>Secteur</label>
                            <select value={filterSector} onChange={(e) => setFilterSector(e.target.value)}>
                                <option value="all">Tous</option>
                                {[...new Set(rows.map(r => r.secteur).filter(Boolean))].sort().map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div className="ma-filter-group">
                            <label>RSI</label>
                            <select value={filterRSI} onChange={(e) => setFilterRSI(e.target.value)}>
                                <option value="all">Tous</option>
                                <option value="oversold">🟢 Survendu</option>
                                <option value="neutral">⚪ Neutre</option>
                                <option value="overbought">🔴 Suracheté</option>
                            </select>
                        </div>
                        <div className="ma-filter-group">
                            <label>Perf YTD</label>
                            <select value={filterYTD} onChange={(e) => setFilterYTD(e.target.value)}>
                                <option value="all">Tous</option>
                                <option value="positive">📈 Positive</option>
                                <option value="negative">📉 Négative</option>
                            </select>
                        </div>
                        <div className="ma-filter-group">
                            <label>Rendement</label>
                            <select value={filterDivYield} onChange={(e) => setFilterDivYield(e.target.value)}>
                                <option value="all">Tous</option>
                                <option value="high">🔥 &gt;5%</option>
                                <option value="medium">💰 2-5%</option>
                                <option value="low">📉 &lt;2%</option>
                            </select>
                        </div>
                    </div>
                )}

                {/* Sort chips */}
                <div className="ma-sort-chips">
                    {['ytd', 'vol30', 'rsi_last', 'div_yield', 'tech_score'].map(col => (
                        <button
                            key={col}
                            className={`ma-sort-chip ${sortColumn === col ? 'active' : ''}`}
                            onClick={() => {
                                if (sortColumn === col) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                else { setSortColumn(col); setSortDirection('desc'); }
                            }}
                        >
                            {{ ytd: 'YTD', vol30: 'Vol', rsi_last: 'RSI', div_yield: 'Dividende', tech_score: 'Score' }[col]}
                            {sortColumn === col && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
                        </button>
                    ))}
                </div>

                {/* Stock Cards */}
                <div className="ma-scanner-list">
                    {filteredRows.slice(0, 30).map((p: AnalysisRow) => (
                        <div key={p.symbole} className="ma-stock-card">
                            <div className="ma-stock-header">
                                <div>
                                    <span className="ma-stock-symbol">{p.symbole}</span>
                                    <span className="ma-stock-name">{p.nom}</span>
                                </div>
                                <TechScoreBadge score={p.tech_score} />
                            </div>
                            <div className="ma-stock-metrics">
                                <div className="ma-stock-metric">
                                    <span className="ma-stock-metric-label">YTD</span>
                                    <span className={`ma-stock-metric-value ${(p.ytd ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                                        {(p.ytd ?? 0).toFixed(2)}%
                                    </span>
                                </div>
                                <div className="ma-stock-metric">
                                    <span className="ma-stock-metric-label">Vol30</span>
                                    <span className="ma-stock-metric-value">{p.vol30 ? (p.vol30 * 100).toFixed(1) + '%' : 'N/A'}</span>
                                </div>
                                <div className="ma-stock-metric">
                                    <span className="ma-stock-metric-label">RSI</span>
                                    <span className={`ma-stock-metric-value ${p.rsi_last && p.rsi_last < 30 ? 'positive' : p.rsi_last && p.rsi_last > 70 ? 'negative' : ''}`}>
                                        {p.rsi_last ? p.rsi_last.toFixed(0) : 'N/A'}
                                    </span>
                                </div>
                                <div className="ma-stock-metric">
                                    <span className="ma-stock-metric-label">Div%</span>
                                    <span className="ma-stock-metric-value">{p.div_yield != null ? `${p.div_yield.toFixed(2)}%` : '—'}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {filteredRows.length > 30 && (
                    <div className="ma-scanner-more">
                        Et {filteredRows.length - 30} de plus... Utilisez les filtres pour affiner.
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileAnalysis;
